import { useState, useContext } from 'react';
import { useMediaQuery, AppBar, Toolbar, IconButton, Box, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../Logo';
import SidebarList from '@/common/presentation/components/layout/Sidebar/SidebarList';
import SidebarNavUser from '@/common/presentation/components/layout/Sidebar/SidebarList/SidebarNavUser';
import { SidebarNavItemCollapseContext, SidebarContext } from '@/common/presentation/components/layout/Sidebar/context';
import { useLocation } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { SIDEBAR_COLORS } from '@/common/presentation/constants/colors';
import authServices from '@/auth/main/DependencyInjection';
import tid from '@/common/infrastructure/config/tid';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: SIDEBAR_COLORS.BACKGROUND,
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  zIndex: theme.zIndex.appBar
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: SIDEBAR_COLORS.BACKGROUND,
    width: 280,
    border: 'none',
    boxShadow: `2px 0 8px ${alpha(theme.palette.common.black, 0.15)}`,
    zIndex: theme.zIndex.appBar + 1,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { authDomainService, tokenService } = authServices;

  // Get username from token instead of localStorage to avoid corruption
  const getUserNameFromToken = () => {
    const token = tokenService.getToken();
    if (token) {
      const user = authDomainService.extractUserFromToken(token);
      return user?.userName || null;
    }
    return null;
  };

  const userName = getUserNameFromToken() || 'User';
  const { pathname } = useLocation();
  const { setCurrentMenuActive } = useContext(SidebarNavItemCollapseContext);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
    setCurrentMenuActive(pathname.replace('/', ''));
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <>
      <StyledAppBar position="fixed">
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            {...tid('common:layout:header:drawer-toggle-button')}
          >
            <MenuIcon />
          </IconButton>

          <Logo expanded={false} />
        </Toolbar>
      </StyledAppBar>

      <StyledDrawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true // Better open performance on mobile
        }}
      >
        <SidebarContext.Provider value={{ sidebarOpen: true, setSidebarOpen: () => {} }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <SidebarList />
            </Box>
            <SidebarNavUser name={userName} />
          </Box>
        </SidebarContext.Provider>
      </StyledDrawer>
    </>
  );
};

export default Header;
